import { supabase } from '../lib/supabase';
import { TimeEntry, TimeCategory, Tag } from '../components/TimeTracking/types';
import { CreateTimeEntry } from '../types/calendar';

// Configuração padrão dos headers
const DEFAULT_OPTIONS = {
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'Prefer': 'return=representation'
  }
};

export interface CreateTimeEntryData {
  organization_id: string;
  category_id: string;
  description?: string;
}

export interface TimeAllocation {
  id: string;
  user_id: string;
  organization_id: string;
  project_id?: string;
  task_id?: string;
  planned_date: string;
  planned_duration: number;
  actual_duration?: number;
  status?: 'planned' | 'in_progress' | 'completed' | 'cancelled';
}

interface TimeEntryFilter {
  startDate: string;
  endDate: string;
  projectId?: string;
  userId?: string;
}

type UpdateTimeEntry = Partial<CreateTimeEntry>;

export class TimeEntryService {
  // Obter categorias
  async getCategories() {
    return []; // Temporariamente retornando array vazio
  }

  // Obter registro atual
  async getCurrentTimeEntry(userId: string): Promise<TimeEntry | null> {
    console.log('Buscando registro atual para usuário:', userId);

    try {
      // maybeSingle() em vez de single(): não existir timer ativo é um resultado
      // válido (0 linhas), não uma condição de erro — evita ter que checar o
      // código de erro PGRST116 manualmente.
      const { data, error } = await supabase
        .from('time_entries')
        .select('*')
        .eq('user_id', userId)
        .is('end_time', null)
        .maybeSingle();

      if (error) {
        console.error('Erro ao buscar registro atual:', error);
        throw new Error(`Falha ao buscar registro atual: ${error.message}`);
      }

      return data || null;
    } catch (e) {
      console.error('Exceção ao buscar registro atual:', e);
      throw e;
    }
  }

  // Iniciar rastreamento
  async startTimeTracking(
    userId: string,
    organizationId: string,
    data: { 
      description?: string; 
      project_id?: string; 
      activity_id?: string | null;
      kanban_task_id?: string;
      tags?: string[];
      billable?: boolean;
    }
  ): Promise<TimeEntry> {
    console.log('Iniciando rastreamento para usuário:', userId, 'com dados:', data);
    
    try {
      // Verificar se já existe um registro em andamento
      const currentEntry = await this.getCurrentTimeEntry(userId);
      if (currentEntry) {
        throw new Error('Já existe um registro de tempo em andamento');
      }
      
      // Extract tags for later use
      const tags = data.tags;
      
      // Criar objeto para inserção com dados básicos (without tags)
      const insertData = {
        user_id: userId,
        organization_id: organizationId,
        start_time: new Date().toISOString(),
        description: data.description || '',
        project_id: data.project_id || null,
        activity_id: data.activity_id || null,
        ...(data.kanban_task_id ? { kanban_task_id: data.kanban_task_id } : {}),
        billable: data.billable || false,
        end_time: null,
        duration: undefined // Usando undefined em vez de null para compatibilidade de tipos
      };
      
      console.log('Inserindo novo registro com dados:', insertData);
      
      let { data: newEntry, error } = await supabase
        .from('time_entries')
        .insert(insertData)
        .select()
        .single();

      // Banco ainda sem a coluna kanban_task_id (migration 20261026000001):
      // grava sem ela para o rastreamento não parar.
      if (error && 'kanban_task_id' in insertData && error.message?.includes('kanban_task_id')) {
        const withoutTask: Record<string, unknown> = { ...insertData };
        delete withoutTask.kanban_task_id;
        ({ data: newEntry, error } = await supabase
          .from('time_entries')
          .insert(withoutTask)
          .select()
          .single());
      }

      if (error) {
        console.error('Erro ao criar registro:', error);
        throw new Error(`Falha ao criar registro: ${error.message}`);
      }

      console.log('Registro criado:', newEntry);

      // Se houver tags, criar as associações
      if (tags && tags.length > 0) {
        console.log('Criando associações de tags:', tags);
        await this.updateTimeEntryTags(newEntry.id, tags);
      }
      
      console.log('Registro criado com sucesso:', newEntry);
      return {
        ...newEntry,
        tags: tags || []
      } as TimeEntry;
    } catch (e) {
      console.error('Exceção ao iniciar rastreamento:', e);
      throw e;
    }
  }

  // Parar rastreamento
  async stopTimeTracking(userId: string): Promise<TimeEntry> {
    try {
      console.log('Service: Parando rastreamento para usuário:', userId);
      
      // 1. Encontrar a entrada ativa
      const { data: activeEntries, error: fetchError } = await supabase
        .from('time_entries')
        .select('*')
        .eq('user_id', userId)
        .is('end_time', null)
        .order('start_time', { ascending: false })
        .limit(1);
      
      if (fetchError) {
        console.error('Erro ao buscar entrada ativa:', fetchError);
        throw new Error(`Erro ao buscar entrada ativa: ${fetchError.message}`);
      }
      
      if (!activeEntries || activeEntries.length === 0) {
        console.error('Nenhuma entrada ativa encontrada para o usuário');
        throw new Error('Nenhuma entrada ativa encontrada');
      }
      
      const activeEntry = activeEntries[0];
      console.log('Entrada ativa encontrada:', activeEntry);
      
      // 2. Atualizar a entrada com o horário de término
      const endTime = new Date().toISOString();
      const duration = this.calculateDuration(activeEntry.start_time, endTime);
      
      const { data: updatedEntry, error: updateError } = await supabase
        .from('time_entries')
        .update({ 
          end_time: endTime,
          duration: duration
        })
        .eq('id', activeEntry.id)
        .select()
        .single();
      
      if (updateError) {
        console.error('Erro ao atualizar entrada:', updateError);
        throw new Error(`Erro ao atualizar entrada: ${updateError.message}`);
      }
      
      console.log('Entrada atualizada com sucesso:', updatedEntry);
      return updatedEntry;
    } catch (err) {
      console.error('Erro ao parar rastreamento:', err);
      throw err;
    }
  }

  // Função para calcular a duração em minutos (não mais em segundos)
  calculateDuration(startTime: string, endTime: string): number {
    const start = new Date(startTime);
    const end = new Date(endTime);
    // Converter de milissegundos para minutos (arredondando para o minuto mais próximo)
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
  }

  // Buscar registros de tempo
  async getTimeEntries(organizationId: string, filter: TimeEntryFilter): Promise<TimeEntry[]> {
    console.log('🔍 TimeEntryService.getTimeEntries:', {
      organizationId,
      filter,
      startDateReadable: new Date(filter.startDate).toLocaleDateString('pt-BR'),
      endDateReadable: new Date(filter.endDate).toLocaleDateString('pt-BR')
    });

    try {
      // Primeiro, buscar as time entries com projetos
      let query = supabase
        .from('time_entries')
        .select(`
          *,
          project:projects(id, name)
        `)
        .eq('organization_id', organizationId)
        .gte('start_time', filter.startDate)
        .lte('start_time', filter.endDate);

      if (filter.projectId) {
        query = query.eq('project_id', filter.projectId);
      }

      if (filter.userId) {
        query = query.eq('user_id', filter.userId);
      }

      const { data: entries, error } = await query;

      if (error) {
        console.error('❌ Erro ao buscar time entries:', error);
        throw new Error(`Falha ao buscar time entries: ${error.message}`);
      }

      console.log(`✅ TimeEntryService: ${entries?.length || 0} entradas encontradas no banco`);

      // Tags de todas as entradas numa consulta só (antes era uma por entrada)
      const tagsByEntry = new Map<string, string[]>();
      const entryIds = (entries || []).map((entry: TimeEntry) => entry.id);
      // Lotes de 150 ids: a lista vai na URL e passar de uns 8 KB derruba a requisição
      const batches: string[][] = [];
      for (let i = 0; i < entryIds.length; i += 150) batches.push(entryIds.slice(i, i + 150));
      const tagResults = await Promise.all(batches.map(ids =>
        supabase.from('time_entry_tags').select('time_entry_id, tag_id').in('time_entry_id', ids)
      ));
      for (const { data: tagRows, error: tagsError } of tagResults) {
        if (tagsError) {
          console.warn('Erro ao buscar tags das entradas:', tagsError);
        }
        for (const row of (tagRows || []) as { time_entry_id: string; tag_id: string }[]) {
          tagsByEntry.set(row.time_entry_id, [...(tagsByEntry.get(row.time_entry_id) || []), row.tag_id]);
        }
      }
      const entriesWithTags = (entries || []).map((entry: TimeEntry) => ({
        ...entry,
        tags: tagsByEntry.get(entry.id) || []
      }));

      return entriesWithTags;
    } catch (e) {
      console.error('Exceção ao buscar time entries:', e);
      throw e;
    }
  }

  // Atualizar registro
  async updateTimeEntry(id: string, updates: UpdateTimeEntry): Promise<TimeEntry> {
    try {
      // Verificar autenticação
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Usuário não autenticado');
      }

      const { data, error } = await supabase
        .from('time_entries')
        .update(updates)
        .eq('id', id)
        .select(`
          *,
          project:projects(id, name)
        `)
        .single();

      if (error) {
        throw new Error(`Falha ao atualizar registro: ${error.message}`);
      }

      if (!data) {
        throw new Error('Nenhum dado retornado da atualização');
      }

      return data;
    } catch (e) {
      console.error('Erro ao atualizar registro:', e);
      throw e;
    }
  }

  // Deletar registro
  async deleteTimeEntry(id: string): Promise<void> {
    try {
      console.log('=== INICIANDO DELETE ===');
      console.log('ID do registro:', id);

      // Primeiro, verificar se o registro existe e se pertence ao usuário
      const { data: existingEntry, error: fetchError } = await supabase
        .from('time_entries')
        .select('id, user_id, organization_id')
        .eq('id', id)
        .single();

      if (fetchError) {
        console.error('Erro ao buscar registro antes de deletar:', fetchError);
        throw new Error(`Registro não encontrado: ${fetchError.message}`);
      }

      console.log('Registro encontrado:', existingEntry);

      // Verificar autenticação
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        console.error('Erro de autenticação:', authError);
        throw new Error('Usuário não autenticado');
      }

      console.log('Usuário autenticado:', user.id);
      console.log('User ID do registro:', existingEntry.user_id);

      if (existingEntry.user_id !== user.id) {
        throw new Error('Você não tem permissão para deletar este registro');
      }

      // PRIMEIRO: Deletar as tags associadas
      console.log('Deletando tags associadas...');
      const { error: tagsDeleteError } = await supabase
        .from('time_entry_tags')
        .delete()
        .eq('time_entry_id', id);

      if (tagsDeleteError) {
        console.error('Erro ao deletar tags:', tagsDeleteError);
        throw new Error(`Falha ao deletar tags: ${tagsDeleteError.message}`);
      }

      console.log('Tags deletadas com sucesso');

      // DEPOIS: Deletar o registro de tempo
      console.log('Tentando deletar o registro...');
      const { error } = await supabase
        .from('time_entries')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro do Supabase ao deletar:', error);
        console.error('Código do erro:', error.code);
        console.error('Detalhes do erro:', error.details);
        console.error('Hint do erro:', error.hint);
        throw new Error(`Falha ao deletar registro: ${error.message}`);
      }

      console.log('=== DELETE CONCLUÍDO COM SUCESSO ===');
    } catch (e) {
      console.error('=== EXCEÇÃO NO DELETE ===');
      console.error('Erro ao deletar registro:', e);
      throw e;
    }
  }

  // Criar uma nova categoria
  async createCategory(
    organizationId: string,
    name: string,
    color: string
  ): Promise<TimeCategory> {
    console.log(`Creating category: ${name} with color ${color} for org ${organizationId}`);
    
    const { data, error } = await supabase
      .from('time_tags')
      .insert({
        organization_id: organizationId,
        name,
        color
      })
      .select()
      .single();
      
    if (error) {
      console.error('Error creating category:', error);
      throw new Error(`Failed to create category: ${error.message}`);
    }
    
    console.log('Category created successfully:', data);
    return data as TimeCategory;
  }

  // Atualizar uma categoria
  async updateCategory(
    categoryId: string,
    updates: Partial<TimeCategory>
  ): Promise<TimeCategory> {
    console.log(`Updating category ${categoryId} with:`, updates);
    
    const { data, error } = await supabase
      .from('time_tags')
      .update(updates)
      .eq('id', categoryId)
      .select()
      .single();
      
    if (error) {
      console.error('Error updating category:', error);
      throw new Error(`Failed to update category: ${error.message}`);
    }
    
    console.log('Category updated successfully:', data);
    return data as TimeCategory;
  }

  // Excluir uma categoria
  async deleteCategory(categoryId: string): Promise<void> {
    console.log(`Deleting category ${categoryId}`);
    
    const { error } = await supabase
      .from('time_tags')
      .delete()
      .eq('id', categoryId);
      
    if (error) {
      console.error('Error deleting category:', error);
      throw new Error(`Failed to delete category: ${error.message}`);
    }
    
    console.log('Category deleted successfully');
  }

  async createTimeEntry(entry: CreateTimeEntry): Promise<TimeEntry> {
      const { data, error } = await supabase
        .from('time_entries')
      .insert([entry])
        .select()
        .single();

      if (error) {
      console.error('Error creating time entry:', error);
      throw error;
    }

    return data;
  }

  async getTimeEntriesWithCategories(organizationId: string): Promise<any[]> {
    const { data: entries, error: entriesError } = await supabase
      .from('time_entries')
      .select('*')
      .eq('organization_id', organizationId)
      .order('start_time', { ascending: false });

    if (entriesError) throw entriesError;
    
    const { data: categories, error: categoriesError } = await supabase
      .from('time_tags')
      .select('*')
      .eq('organization_id', organizationId);
      
    if (categoriesError) throw categoriesError;
    
    // Combinar manualmente
    return (entries || []).map(entry => {
      const category = categories?.find(c => c.id === entry.category_id) || null;
      return {
        ...entry,
        category
      };
    });
  }

  // Buscar tags da organização
  async getTags(organizationId: string): Promise<Tag[]> {
    const { data, error } = await supabase
      .from('time_tags')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name');

    if (error) {
      console.error('Erro ao buscar tags:', error);
      throw error;
    }

    return data || [];
  }

  // Criar uma nova tag
  async createTag(tag: Omit<Tag, 'id' | 'created_at' | 'updated_at'>): Promise<Tag> {
    const { data, error } = await supabase
      .from('time_tags')
      .insert(tag)
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar tag:', error);
      throw error;
    }

    return data;
  }

  // Atualizar uma tag existente
  async updateTag(id: string, updates: Partial<Omit<Tag, 'id' | 'created_at' | 'updated_at'>>): Promise<Tag> {
    const { data, error } = await supabase
      .from('time_tags')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar tag:', error);
      throw error;
    }

    return data;
  }

  // Excluir uma tag
  async deleteTag(id: string): Promise<void> {
    const { error } = await supabase
      .from('time_tags')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao excluir tag:', error);
      throw error;
    }
  }

  // Atualizar tags de um registro de tempo
  async updateTimeEntryTags(timeEntryId: string, tagIds: string[]): Promise<void> {
    try {
      console.log('Updating tags for entry:', timeEntryId);
      console.log('New tag IDs:', tagIds);
      
      // First, get the organization_id of the time entry
      const { data: timeEntry, error: timeEntryError } = await supabase
        .from('time_entries')
        .select('organization_id')
        .eq('id', timeEntryId)
        .single();

      if (timeEntryError) {
        console.error('Error fetching time entry:', timeEntryError);
        throw new Error(`Could not get organization from entry: ${timeEntryError.message}`);
      }

      if (!timeEntry || !timeEntry.organization_id) {
        throw new Error('Entry does not have a valid organization_id');
      }

      // First, remove all existing associations
      console.log('Deleting existing tag associations...');
      const { error: deleteError } = await supabase
        .from('time_entry_tags')
        .delete()
        .eq('time_entry_id', timeEntryId);

      if (deleteError) {
        console.error('Error removing old tags:', deleteError);
        throw new Error(`Failed to remove old tags: ${deleteError.message}`);
      }
      console.log('Existing tag associations deleted successfully');

      // If there are no new tags, end here
      if (!tagIds || tagIds.length === 0) {
        console.log('No tags to add, operation completed');
        return;
      }

      // Check if the tags exist
      console.log('Verifying tags exist in database...');
      const { data: existingTags, error: existingTagsError } = await supabase
        .from('time_tags')
        .select('id')
        .in('id', tagIds)
        .eq('organization_id', timeEntry.organization_id);
      
      if (existingTagsError) {
        console.error('Error checking existing tags:', existingTagsError);
        throw new Error(`Failed to verify existing tags: ${existingTagsError.message}`);
      }
      
      // Filter only the tags that actually exist
      const validTagIds = (existingTags || []).map(tag => tag.id);
      console.log('Valid tag IDs:', validTagIds);
      
      if (validTagIds.length === 0) {
        console.log('No valid tags found, operation completed');
        return;
      }

      // Create new associations with valid tags
      console.log('Creating new tag associations...');
      const tagAssociations = validTagIds.map(tagId => ({
        time_entry_id: timeEntryId,
        tag_id: tagId,
        organization_id: timeEntry.organization_id
      }));

      const { data: insertData, error: insertError } = await supabase
        .from('time_entry_tags')
        .insert(tagAssociations)
        .select();

      if (insertError) {
        console.error('Error inserting new tags:', insertError);
        throw new Error(`Failed to insert new tags: ${insertError.message}`);
      }

      console.log('Tags updated successfully:', insertData);
    } catch (error) {
      console.error('Error updating tags:', error);
      throw error;
    }
  }

  // Buscar alocações de tempo
  async getTimeAllocations(
    organizationId: string,
    filters: {
      userId?: string;
      startDate?: string;
      endDate?: string;
      projectId?: string;
      taskId?: string;
      status?: string;
    } = {}
  ): Promise<TimeAllocation[]> {
    let query = supabase
      .from('time_allocations')
      .select('*')
      .eq('organization_id', organizationId)
      .order('planned_date', { ascending: true });

    // Aplicar filtros
    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters.startDate) {
      query = query.gte('planned_date', filters.startDate);
    }
    if (filters.endDate) {
      query = query.lte('planned_date', filters.endDate);
    }
    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }
    if (filters.taskId) {
      query = query.eq('task_id', filters.taskId);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Erro ao buscar alocações de tempo:', error);
      throw error;
    }

    return data || [];
  }

  // Criar uma nova alocação de tempo
  async createTimeAllocation(allocation: Omit<TimeAllocation, 'id' | 'created_at' | 'updated_at'>): Promise<TimeAllocation> {
    const { data, error } = await supabase
      .from('time_allocations')
      .insert(allocation)
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar alocação de tempo:', error);
      throw error;
    }

    return data;
  }

  // Atualizar uma alocação de tempo existente
  async updateTimeAllocation(
    id: string,
    updates: Partial<Omit<TimeAllocation, 'id' | 'created_at' | 'updated_at'>>
  ): Promise<TimeAllocation> {
    const { data, error } = await supabase
      .from('time_allocations')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar alocação de tempo:', error);
      throw error;
    }

    return data;
  }

  // Excluir uma alocação de tempo
  async deleteTimeAllocation(id: string): Promise<void> {
    const { error } = await supabase
      .from('time_allocations')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao excluir alocação de tempo:', error);
      throw error;
    }
  }

  // Gerar relatório de tempo por projeto
  async getTimeReportByProject(
    organizationId: string,
    startDate: string,
    endDate: string
  ): Promise<{ project_id: string; total_duration: number }[]> {
    const { data, error } = await supabase
      .rpc('get_time_by_project', {
        p_organization_id: organizationId,
        p_start_date: startDate,
        p_end_date: endDate
      });

    if (error) {
      console.error('Erro ao gerar relatório por projeto:', error);
      throw error;
    }

    return data || [];
  }

  // Gerar relatório de tempo por usuário
  async getTimeReportByUser(
    organizationId: string,
    startDate: string,
    endDate: string
  ): Promise<{ user_id: string; total_duration: number }[]> {
    const { data, error } = await supabase
      .rpc('get_time_by_user', {
        p_organization_id: organizationId,
        p_start_date: startDate,
        p_end_date: endDate
      });

    if (error) {
      console.error('Erro ao gerar relatório por usuário:', error);
      throw error;
    }

    return data || [];
  }

  // Duplicate a time entry with all its attributes
  async duplicateTimeEntry(entryId: string, userId: string): Promise<TimeEntry> {
    try {
      console.log('Duplicating time entry:', entryId, 'for user:', userId);
      
      // Fetch the original entry with all details
      const { data: originalEntry, error } = await supabase
        .from('time_entries')
        .select('*')
        .eq('id', entryId)
        .single();
        
      if (error) {
        console.error('Error fetching original entry:', error);
        throw new Error(`Failed to fetch original entry: ${error.message}`);
      }
      
      if (!originalEntry) {
        console.error('Original entry not found in database for ID:', entryId);
        throw new Error('Original entry not found');
      }
      
      console.log('Original entry found:', originalEntry);
      
      // Create a new entry object with the same data but without id
      const newEntryData = {
        user_id: userId, // Use the current user's ID
        organization_id: originalEntry.organization_id,
        description: originalEntry.description,
        project_id: originalEntry.project_id,
        activity_id: originalEntry.activity_id,
        start_time: originalEntry.start_time,
        end_time: originalEntry.end_time,
        duration: originalEntry.duration,
        billable: originalEntry.billable || false
      };
      
      console.log('Creating duplicate entry with data:', newEntryData);
      
      // Insert the new entry
      const { data: newEntry, error: insertError } = await supabase
        .from('time_entries')
        .insert(newEntryData)
        .select()
        .single();
        
      if (insertError) {
        console.error('Error creating duplicate entry:', insertError);
        throw new Error(`Failed to create duplicate entry: ${insertError.message}`);
      }
      
      console.log('Duplicate entry created:', newEntry);
      
      // Fetch tags for the original entry
      const { data: tagAssociations, error: tagsError } = await supabase
        .from('time_entry_tags')
        .select('tag_id')
        .eq('time_entry_id', entryId);
        
      if (tagsError) {
        console.error('Error fetching tags for original entry:', tagsError);
        // Continue without tags rather than failing
      }
      
      // Copy tags if there are any
      if (tagAssociations && tagAssociations.length > 0) {
        const tags = tagAssociations.map(ta => ta.tag_id);
        console.log('Copying tags to new entry:', tags);
        await this.updateTimeEntryTags(newEntry.id, tags);
      }
      
      // Return the new entry with tags
      return {
        ...newEntry,
        tags: tagAssociations?.map(ta => ta.tag_id) || []
      } as TimeEntry;
    } catch (error) {
      console.error('Error duplicating time entry:', error);
      throw error;
    }
  }
}

export const timeEntryService = new TimeEntryService(); 